import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  List,
  ListItemText,
  ListItemButton,
  Divider,
  InputAdornment,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { searchCustomers } from '../../customers/services/customerService';

const CustomerDialog = ({ open, onClose, onSelectCustomer, onCreateNewCustomer, currentCustomer, container, style }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // Reset when dialog opens
  useEffect(() => {
    if (open) {
      setSearchQuery('');
      setCustomers([]);
      setSelectedCustomer(currentCustomer || null);
    }
  }, [open, currentCustomer]);

  // Debounced search
  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      if (searchQuery.trim().length >= 3) {
        handleSearch();
      } else if (searchQuery.trim().length === 0) {
        setCustomers([]);
      }
    }, 500);

    return () => clearTimeout(delayedSearch);
  }, [searchQuery]);

  const handleSearch = async () => {
    if (searchQuery.trim().length < 3) {
      toast.info('Please enter at least 3 characters to search');
      return;
    }

    try {
      setLoading(true);
      
      const results = await searchCustomers(searchQuery);
      
      setCustomers(results || []);
      
      if (!results || results.length === 0) {
        toast.info('No customers found');
      }
    } catch (error) {
      console.error('❌ Customer search error:', error);
      toast.error('Failed to search customers');
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCustomerClick = (customer) => {
    setSelectedCustomer(customer);
  };

  const handleConfirmSelection = () => {
    if (selectedCustomer) {
      onSelectCustomer(selectedCustomer);
      toast.success(`Customer selected: ${selectedCustomer.Name}`);
      onClose(); // ✅ Close dialog after selection
    }
  };

  const handleRemoveCustomer = () => {
    onSelectCustomer(null);
    toast.info('Customer removed from sale');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      container={container}
      style={style}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 2,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonIcon />
          <Typography variant="h6" fontWeight={600}>
            Select Customer
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3 }}>
        {/* Search Box */}
        <TextField
          fullWidth
          placeholder="Search by name, email, VAT number, or ID..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          autoFocus
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: loading && (
              <InputAdornment position="end">
                <CircularProgress size={20} />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />

        {/* Instructions */}
        {searchQuery.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <SearchIcon sx={{ fontSize: 64, color: 'text.secondary', opacity: 0.3 }} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Enter at least 3 characters to search for customers
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Search by name, email, VAT number, or customer ID
            </Typography>
          </Box>
        )}

        {/* No Results */}
        {searchQuery.length >= 3 && !loading && customers.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <PersonIcon sx={{ fontSize: 64, color: 'text.secondary', opacity: 0.3 }} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              No customers found for "{searchQuery}"
            </Typography>
          </Box>
        )}

        {/* Customer List */}
        {customers.length > 0 && (
          <List sx={{ maxHeight: 400, overflow: 'auto' }}>
            {customers.map((customer) => (
              <React.Fragment key={customer.Id}>
                <ListItemButton
                  selected={selectedCustomer?.Id === customer.Id}
                  onClick={() => handleCustomerClick(customer)}
                  sx={{
                    borderRadius: 1,
                    mb: 1,
                    '&.Mui-selected': {
                      bgcolor: 'primary.light',
                      '&:hover': {
                        bgcolor: 'primary.light',
                      },
                    },
                  }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="subtitle1" fontWeight={600}>
                          {customer.CompanyName || customer.Name}
                        </Typography>
                        <Chip label={`ID: ${customer.Id}`} size="small" />
                      </Box>
                    }
                    secondary={
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1 }}>
                        {customer.Email && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <EmailIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                              {customer.Email}
                            </Typography>
                          </Box>
                        )}
                        {customer.Phone && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <PhoneIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                              {customer.Phone}
                            </Typography>
                          </Box>
                        )}
                        {customer.VATNumber && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <BusinessIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                              VAT: {customer.VATNumber}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    }
                  />
                </ListItemButton>
                <Divider />
              </React.Fragment>
            ))}
          </List>
        )}

        {/* Selected Customer Info */}
        {selectedCustomer && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              bgcolor: 'success.light',
              borderRadius: 2,
              border: '2px solid',
              borderColor: 'success.main',
            }}
          >
            <Typography variant="subtitle2" fontWeight={600} gutterBottom color="success.dark">
              ✓ Selected Customer
            </Typography>
            <Typography variant="body1" fontWeight={600}>
              {selectedCustomer.Name}
            </Typography>
            {selectedCustomer.Email && (
              <Typography variant="body2" color="text.secondary">
                {selectedCustomer.Email}
              </Typography>
            )}
            {selectedCustomer.Phone && (
              <Typography variant="body2" color="text.secondary">
                Phone: {selectedCustomer.Phone}
              </Typography>
            )}
            {selectedCustomer.VATNumber && (
              <Typography variant="body2" color="text.secondary">
                VAT: {selectedCustomer.VATNumber}
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        {currentCustomer && (
          <Button onClick={handleRemoveCustomer} variant="outlined" color="error">
            Remove Customer
          </Button>
        )}
        {onCreateNewCustomer && (
          <Button 
            onClick={onCreateNewCustomer} 
            variant="outlined" 
            color="primary"
            sx={{ mr: 'auto' }}
          >
            + Create New Customer
          </Button>
        )}
        <Button onClick={onClose} variant="outlined" color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleConfirmSelection}
          variant="contained"
          disabled={!selectedCustomer}
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            minWidth: 120,
            '&:hover': {
              background: 'linear-gradient(135deg, #5568d3 0%, #6a3d8f 100%)',
            },
          }}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustomerDialog;